/**
 * The icon set, drawn inline.
 *
 * Every glyph is a 24-unit stroke drawing on `currentColor`, so an icon takes
 * the colour and the size of the text beside it and nothing has to be
 * re-exported in a second colour for the sidebar's active state. Inline SVG
 * rather than a font or a package: six icons do not justify a dependency, and
 * a stroke that scales with `--icon` keeps a 16 px row and a 20 px header
 * looking like the same drawing.
 */
import type { SVGProps } from "react";

export type IconProps = SVGProps<SVGSVGElement> & { size?: number };

/** The frame every glyph is drawn in — exported so the constructor's own set
 *  (`design/icons.tsx`) is the same drawing at a different subject. */
export function Svg({ size = 18, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ── Customer dashboard navigation ─────────────────────────────────────── */

/** Devices: a phone, because the fleet is phones. */
export function IconDevices(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="6" y="2.5" width="12" height="19" rx="2.6" />
      <path d="M10.5 5.6h3" />
      <path d="M10.8 18.4h2.4" />
    </Svg>
  );
}

/** Triggers: a bolt — something fires on its own. */
export function IconTriggers(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M13.4 2.5 4.8 13.2h6L10.6 21.5l8.6-10.7h-6z" />
    </Svg>
  );
}

/** Analyze: a target — a set of criteria a phone either matches or does not. */
export function IconAnalyzes(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Agent APK: a box that gets shipped to a phone. */
export function IconPackage(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20.5 7.7v8.6a1.6 1.6 0 0 1-.85 1.4l-6.9 3.8a1.6 1.6 0 0 1-1.5 0l-6.9-3.8a1.6 1.6 0 0 1-.85-1.4V7.7" />
      <path d="M3.9 7 11.25 3a1.6 1.6 0 0 1 1.5 0L20.1 7l-7.35 4a1.6 1.6 0 0 1-1.5 0z" />
      <path d="M12 11.3v9.4" />
    </Svg>
  );
}

/** Ready builds: a library shelf — finished APKs waiting to be handed out. */
export function IconLibrary(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 4h17v4.5h-17z" />
      <path d="M4.8 8.5V19a1.5 1.5 0 0 0 1.5 1.5h11.4a1.5 1.5 0 0 0 1.5-1.5V8.5" />
      <path d="M10 11.5h4" />
    </Svg>
  );
}

/** App screen: a phone laid out in blocks — the constructor's own picture. */
export function IconDesign(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M3 9h18" />
      <path d="M9.5 21V9" />
    </Svg>
  );
}

/** Modules: blocks that snap onto the app — what a module actually is. */
export function IconModules(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="8" height="8" rx="2" />
      <rect x="13" y="3" width="8" height="8" rx="2" />
      <rect x="3" y="13" width="8" height="8" rx="2" />
      <path d="M17 13.5v7M13.5 17h7" />
    </Svg>
  );
}

/** Settings: sliders, not a cog — these are values to move, not machinery. */
export function IconSettings(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 5v6M5 15v4M12 5v3M12 12v7M19 5v9M19 18v1" />
      <circle cx="5" cy="13" r="2" />
      <circle cx="12" cy="10" r="2" />
      <circle cx="19" cy="16" r="2" />
    </Svg>
  );
}

/* ── Admin console navigation ──────────────────────────────────────────── */

/** Customers: an organisation, not a person. */
export function IconTenants(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 20.5h17" />
      <path d="M5.5 20.5V5.2a1.2 1.2 0 0 1 .78-1.12l6-2.05a1.2 1.2 0 0 1 1.62 1.12V20.5" />
      <path d="M13.9 20.5V9.4h3.9a1.2 1.2 0 0 1 1.2 1.2v9.9" />
      <path d="M8.6 7.6h2.3M8.6 11.2h2.3M8.6 14.8h2.3" />
    </Svg>
  );
}

/** Desktop apps: a computer on a stand — the dashboard in its own window. */
export function IconDesktop(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
      <path d="M12 16.5v3.5" />
      <path d="M8.5 20h7" />
    </Svg>
  );
}

/** Audit: a log — lines of things that happened. */
export function IconAudit(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M5 4.5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1z" />
      <path d="M8 9h8M8 12.5h8M8 16h5" />
    </Svg>
  );
}

/** Admins: a shield — who is allowed through our own door. */
export function IconShield(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 2.6 4.8 5.4v6c0 4.3 3 8.3 7.2 9.9 4.2-1.6 7.2-5.6 7.2-9.9v-6z" />
      <path d="m9.2 11.9 2 2.1 3.6-4" />
    </Svg>
  );
}

/* ── Chrome ────────────────────────────────────────────────────────────── */

export function IconSignOut(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14.5 4.5h3.2a1.3 1.3 0 0 1 1.3 1.3v12.4a1.3 1.3 0 0 1-1.3 1.3h-3.2" />
      <path d="M10 15.8 13.8 12 10 8.2" />
      <path d="M13.5 12H4.6" />
    </Svg>
  );
}

export function IconMenu(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Svg>
  );
}

export function IconClose(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m6.5 6.5 11 11M17.5 6.5l-11 11" />
    </Svg>
  );
}

/* ── The fleet list ────────────────────────────────────────────────────────
   A collapsed device row says everything it can as a glyph rather than as a
   sentence: four grants, a chevron and a warning fit where "✗ Keyboard
   ✗ Notifications" did not. The words are not lost — they are the `title` of
   each glyph, and they are written out in full in the row's own detail
   panel. */

/** The row expander. One drawing, turned by CSS when the row opens, so the
 *  open and closed states cannot drift apart. */
export function IconChevron(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m9 5.5 7 6.5-7 6.5" />
    </Svg>
  );
}

/** Accessibility: the universal access figure. The grant the whole agent
 *  stands on, so it is the one glyph an operator learns first. */
export function IconAccessibility(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="4.6" r="1.9" />
      <path d="M4.5 8.4h15" />
      <path d="M12 8.4v6.2" />
      <path d="m8.6 21 3.4-6.4 3.4 6.4" />
    </Svg>
  );
}

/** Screen capture: a phone with a frame being taken off it. */
export function IconCapture(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="4.5" width="13" height="15" rx="2.2" />
      <path d="M19.5 9v10.5a1.5 1.5 0 0 1-1.5 1.5H8" />
    </Svg>
  );
}

/** The agent's own keyboard. */
export function IconKeyboard(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="2.5" y="6" width="19" height="12" rx="2.2" />
      <path d="M6.5 10h.01M10 10h.01M13.5 10h.01M17 10h.01M8 14h8" />
    </Svg>
  );
}

/** Notifications: a bell, and the only optional grant of the four. */
export function IconBell(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9Z" />
      <path d="M13.7 19a2 2 0 0 1-3.4 0" />
    </Svg>
  );
}

/** Something is wrong with this phone; the reason is on hover and in full in
 *  the detail panel. */
export function IconWarning(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 3.8 21 19.5H3L12 3.8Z" />
      <path d="M12 10v4.2M12 17h.01" />
    </Svg>
  );
}

/** The mark: three signal bars rising out of a rounded tile. */
export function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <span className="brand-mark" style={{ width: size, height: size }} aria-hidden="true">
      <svg width={size * 0.56} height={size * 0.56} viewBox="0 0 24 24" fill="none">
        <path
          d="M4 15.5v3.5M10 9.5v9.5M16 12.5v6.5M22 4.5v14.5"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}
